import React, { useRef, useEffect, useState, useCallback, useImperativeHandle } from 'react'

/**
 * DrawingCanvas Component
 * 
 * A 1920x1920px HTML5 Canvas component for drawing with mouse and touch support.
 * Supports basic pen drawing with adjustable brush size and color.
 */
const DrawingCanvas = React.forwardRef(({ 
  width = 1920, 
  height = 1920, 
  onDrawingChange,
  backgroundImage = null,
  mode = 'create',
  currentColor = '#000000',
  brushSize = 3,
  currentTool = 'pen'
}, ref) => {
  const canvasRef = useRef(null)
  const tempCanvasRef = useRef(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [lastPoint, setLastPoint] = useState({ x: 0, y: 0 })
  const [originalImage, setOriginalImage] = useState(null)
  
  // Drawing state
  const [isDrawingShape, setIsDrawingShape] = useState(false)
  const [shapeStartPoint, setShapeStartPoint] = useState({ x: 0, y: 0 })
  const [tempShape, setTempShape] = useState(null)
  const [savedCanvasData, setSavedCanvasData] = useState(null)
  
  // Undo/Redo state
  const [history, setHistory] = useState([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [isUndoRedo, setIsUndoRedo] = useState(false)
  
  // Text tool state
  const [isTextMode, setIsTextMode] = useState(false)
  const [textInput, setTextInput] = useState('')
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 })
  const [textInputRef, setTextInputRef] = useState(null)
  
  // Pan tool state
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState({ x: 0, y: 0 })
  
  // Initialize canvas (only when canvas size, background image, or mode changes)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    
    // Set canvas size
    canvas.width = width
    canvas.height = height
    
    // Set default drawing styles
    ctx.strokeStyle = currentColor
    ctx.lineWidth = brushSize
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    
    // Enable antialiasing for smooth drawing
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    
    // Load background image if provided
    if (backgroundImage && mode === 'edit') {
      loadBackgroundImage(backgroundImage)
    }
    
    // Initialize history with empty canvas state
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    setHistory([imageData])
    setHistoryIndex(0)
  }, [width, height, backgroundImage, mode])

  // Update drawing styles without clearing canvas (when color, brush size, or tool changes)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    
    // Update drawing styles without clearing the canvas
    if (currentTool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out'
    } else {
      ctx.globalCompositeOperation = 'source-over'
      ctx.strokeStyle = currentColor
    }
    ctx.lineWidth = brushSize
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    
    // Ensure antialiasing is enabled for all drawing operations
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
  }, [currentColor, brushSize, currentTool])
  
  /**
   * Load background image for editing mode
   */
  const loadBackgroundImage = useCallback(async (imageSrc) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    
    img.onload = () => {
      setOriginalImage(img)
      drawBackgroundImage(img)
    }
    
    img.onerror = () => {
      console.error('Failed to load background image')
    }
    
    img.src = imageSrc
  }, [])
  
  /**
   * Draw background image scaled to fit canvas
   */
  const drawBackgroundImage = useCallback((img) => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // Enable antialiasing for smooth background image rendering
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    
    // Calculate scaling to fit image in canvas
    const maxSize = Math.min(width, height)
    let { width: imgWidth, height: imgHeight } = img
    
    if (imgWidth > imgHeight) {
      imgHeight = (imgHeight * maxSize) / imgWidth
      imgWidth = maxSize
    } else {
      imgWidth = (imgWidth * maxSize) / imgHeight
      imgHeight = maxSize
    }
    
    // Center the image
    const x = (canvas.width - imgWidth) / 2
    const y = (canvas.height - imgHeight) / 2
    
    // Draw background image
    ctx.drawImage(img, x, y, imgWidth, imgHeight)
    
    // Notify parent of canvas change
    if (onDrawingChange) {
      onDrawingChange(canvas.toDataURL())
    }
  }, [width, height, onDrawingChange])
  
  /**
   * Get mouse/touch position relative to canvas
   */
  const getPointFromEvent = useCallback((e) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    
    let clientX, clientY
    
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    }
  }, [])
  
  /**
   * Fill tool - flood fill algorithm
   */
  const floodFill = useCallback((startX, startY, fillColor) => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const data = imageData.data
    const width = canvas.width
    const height = canvas.height
    
    // Get the color at the starting point
    const startIndex = (startY * width + startX) * 4
    const startR = data[startIndex]
    const startG = data[startIndex + 1]
    const startB = data[startIndex + 2]
    const startA = data[startIndex + 3]
    
    // Convert fill color to RGBA
    const fillR = parseInt(fillColor.slice(1, 3), 16)
    const fillG = parseInt(fillColor.slice(3, 5), 16)
    const fillB = parseInt(fillColor.slice(5, 7), 16)
    const fillA = 255
    
    // If the colors are the same, no need to fill
    if (startR === fillR && startG === fillG && startB === fillB && startA === fillA) {
      return
    }
    
    // Stack-based flood fill
    const stack = [{ x: startX, y: startY }]
    const visited = new Set()
    
    while (stack.length > 0) {
      const { x, y } = stack.pop()
      const key = `${x},${y}`
      
      if (visited.has(key) || x < 0 || x >= width || y < 0 || y >= height) {
        continue
      }
      
      const index = (y * width + x) * 4
      const r = data[index]
      const g = data[index + 1]
      const b = data[index + 2]
      const a = data[index + 3]
      
      // Check if this pixel matches the target color
      if (r === startR && g === startG && b === startB && a === startA) {
        // Fill this pixel
        data[index] = fillR
        data[index + 1] = fillG
        data[index + 2] = fillB
        data[index + 3] = fillA
        
        visited.add(key)
        
        // Add neighboring pixels to stack
        stack.push({ x: x + 1, y })
        stack.push({ x: x - 1, y })
        stack.push({ x, y: y + 1 })
        stack.push({ x, y: y - 1 })
      }
    }
    
    // Put the modified image data back
    ctx.putImageData(imageData, 0, 0)
    
    // Notify parent of canvas change
    if (onDrawingChange) {
      onDrawingChange(canvas.toDataURL())
    }
  }, [onDrawingChange])

  /**
   * Save current canvas state to history
   */
  const saveToHistory = useCallback(() => {
    if (isUndoRedo) return // Don't save during undo/redo operations
    
    const canvas = canvasRef.current
    if (!canvas) return
    
    const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height)
    const newHistory = history.slice(0, historyIndex + 1) // Remove any future history
    newHistory.push(imageData)
    
    // Limit history to 20 states to prevent memory issues
    if (newHistory.length > 20) {
      newHistory.shift()
    } else {
      setHistoryIndex(prev => prev + 1)
    }
    
    setHistory(newHistory)
  }, [history, historyIndex, isUndoRedo])


  /**
   * Handle text tool click
   */
  const handleTextClick = useCallback((e) => {
    if (currentTool !== 'text') return
    
    e.preventDefault()
    const point = getPointFromEvent(e)
    setTextPosition(point)
    setIsTextMode(true)
    setTextInput('')
  }, [currentTool, getPointFromEvent])

  /**
   * Start drawing
   */
  const startDrawing = useCallback((e) => {
    e.preventDefault()
    const point = getPointFromEvent(e)
    
    if (currentTool === 'pen' || currentTool === 'eraser') {
      setIsDrawing(true)
      setLastPoint(point)
      
      const canvas = canvasRef.current
      if (!canvas) return
      
      const ctx = canvas.getContext('2d')
      
      // Set drawing styles
      if (currentTool === 'eraser') {
        ctx.globalCompositeOperation = 'destination-out'
        ctx.lineWidth = brushSize
      } else {
        ctx.globalCompositeOperation = 'source-over'
        ctx.strokeStyle = currentColor
        ctx.lineWidth = brushSize
      }
      
      // Ensure antialiasing is enabled for smooth strokes
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      
      ctx.beginPath()
      ctx.moveTo(point.x, point.y)
      
      // Draw a single point for small brushes
      if (brushSize <= 3) {
        if (currentTool === 'pen') {
          ctx.fillStyle = currentColor
          ctx.arc(point.x, point.y, brushSize / 2, 0, Math.PI * 2)
          ctx.fill()
        } else {
          // For eraser, draw a small circle to erase
          ctx.arc(point.x, point.y, brushSize / 2, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    } else if (['rectangle', 'circle', 'line'].includes(currentTool)) {
      setIsDrawingShape(true)
      setShapeStartPoint(point)
      setTempShape({ type: currentTool, start: point, end: point })
      
      // Save current canvas state before starting shape drawing
      const canvas = canvasRef.current
      if (canvas) {
        const ctx = canvas.getContext('2d')
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        setSavedCanvasData(imageData)
      }
    } else if (currentTool === 'fill') {
      // Handle fill tool
      floodFill(Math.floor(point.x), Math.floor(point.y), currentColor)
    } else if (currentTool === 'text') {
      // Handle text tool
      handleTextClick(e)
    } else if (currentTool === 'pan') {
      // Handle pan tool - start panning
      setIsPanning(true)
      setPanStart({ x: e.clientX, y: e.clientY })
    }
  }, [getPointFromEvent, brushSize, currentColor, currentTool, floodFill, handleTextClick, isPanning, panStart])
  
  /**
   * Continue drawing
   */
  const draw = useCallback((e) => {
    e.preventDefault()
    const point = getPointFromEvent(e)
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    
    if (isPanning && currentTool === 'pan') {
      // Handle panning - scroll the container
      const deltaX = e.clientX - panStart.x
      const deltaY = e.clientY - panStart.y
      
      // Find the scrollable container (the canvas area)
      const container = canvas.closest('.canvas-container')
      if (container) {
        container.scrollLeft -= deltaX
        container.scrollTop -= deltaY
      }
      
      setPanStart({ x: e.clientX, y: e.clientY })
    } else if (isDrawing && (currentTool === 'pen' || currentTool === 'eraser')) {
      // Draw line from last point to current point
      ctx.lineTo(point.x, point.y)
      ctx.stroke()
      
      setLastPoint(point)
    } else if (isDrawingShape && tempShape) {
      // Update temporary shape
      const updatedShape = { ...tempShape, end: point }
      setTempShape(updatedShape)
      
      // Redraw canvas with temporary shape
      redrawCanvasWithTempShape(updatedShape)
    }
    
    // PERFORMANCE: Only notify parent when drawing stops, not on every mouse move
    // This prevents expensive toDataURL() calls during drawing
  }, [isPanning, panStart, isDrawing, isDrawingShape, currentTool, getPointFromEvent, lastPoint, currentColor, brushSize, tempShape])
  
  /**
   * Redraw canvas with temporary shape
   */
  const redrawCanvasWithTempShape = useCallback((shape) => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    
    // Clear canvas completely
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // Restore saved canvas data if available
    if (savedCanvasData) {
      ctx.putImageData(savedCanvasData, 0, 0)
    } else {
      // Redraw background image if in edit mode
      if (originalImage && mode === 'edit') {
        drawBackgroundImage(originalImage)
      }
    }
    
    // Draw temporary shape
    ctx.strokeStyle = currentColor
    ctx.lineWidth = brushSize
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    
    // Enable antialiasing for smooth shapes
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    
    const { start, end, type } = shape
    const width = end.x - start.x
    const height = end.y - start.y
    
    ctx.beginPath()
    
    switch (type) {
      case 'rectangle':
        ctx.rect(start.x, start.y, width, height)
        break
      case 'circle':
        // For circles, start point is the center, end point determines radius
        const radius = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2))
        // Ensure perfect circles by using the same radius for both axes
        ctx.arc(start.x, start.y, radius, 0, Math.PI * 2)
        break
      case 'line':
        ctx.moveTo(start.x, start.y)
        ctx.lineTo(end.x, end.y)
        break
    }
    
    ctx.stroke()
  }, [savedCanvasData, originalImage, mode, drawBackgroundImage, currentColor, brushSize])

  /**
   * Stop drawing
   */
  const stopDrawing = useCallback((e) => {
    e.preventDefault()
    
    if (isPanning) {
      setIsPanning(false)
      setPanStart({ x: 0, y: 0 })
    } else if (isDrawing) {
      setIsDrawing(false)
    } else if (isDrawingShape && tempShape) {
      // Finalize shape
      const canvas = canvasRef.current
      if (!canvas) return
      
      const ctx = canvas.getContext('2d')
      ctx.strokeStyle = currentColor
      ctx.lineWidth = brushSize
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      
      // Enable antialiasing for smooth final shapes
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      
      const { start, end, type } = tempShape
      const width = end.x - start.x
      const height = end.y - start.y
      
      ctx.beginPath()
      
      switch (type) {
        case 'rectangle':
          ctx.rect(start.x, start.y, width, height)
          break
        case 'circle':
          // For circles, start point is the center, end point determines radius
          const radius = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2))
          ctx.arc(start.x, start.y, radius, 0, Math.PI * 2)
          break
        case 'line':
          ctx.moveTo(start.x, start.y)
          ctx.lineTo(end.x, end.y)
          break
      }
      
      ctx.stroke()
      
      // Reset shape drawing state
      setIsDrawingShape(false)
      setTempShape(null)
      setSavedCanvasData(null)
      
      // Save to history after completing shape
      setTimeout(() => saveToHistory(), 50)
    }
    
    // Save to history after completing pen/eraser drawing
    if (isDrawing) {
      setTimeout(() => saveToHistory(), 50)
    }
    
    // PERFORMANCE: Notify parent of canvas change only when drawing stops
    // This prevents expensive toDataURL() calls during drawing
    if (onDrawingChange && (isDrawing || isDrawingShape)) {
      const canvas = canvasRef.current
      if (canvas) {
        onDrawingChange(canvas.toDataURL())
      }
    }
  }, [isDrawing, isDrawingShape, tempShape, currentColor, brushSize, saveToHistory, onDrawingChange])
  
  /**
   * Clear canvas
   */
  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // Redraw background image if in edit mode
    if (originalImage && mode === 'edit') {
      drawBackgroundImage(originalImage)
    }
    
    // Clear saved canvas data
    setSavedCanvasData(null)
    
    // Save to history after clearing
    setTimeout(() => saveToHistory(), 50)
    
    // Notify parent of canvas change
    if (onDrawingChange) {
      onDrawingChange(canvas.toDataURL())
    }
  }, [originalImage, mode, drawBackgroundImage, onDrawingChange, saveToHistory])
  
  /**
   * Reset canvas to original state (edit mode only)
   */
  const resetCanvas = useCallback(() => {
    if (originalImage && mode === 'edit') {
      drawBackgroundImage(originalImage)
    }
  }, [originalImage, mode, drawBackgroundImage])
  
  /**
   * Get canvas as data URL with automatic cropping
   */
  const getCanvasDataUrl = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return null
    
    // Get the bounding box of non-transparent pixels
    const boundingBox = getDrawingBoundingBox(canvas)
    
    if (!boundingBox) {
      // If no content found, return the original canvas
      return canvas.toDataURL('image/png')
    }
    
    // Create a new canvas with the cropped dimensions
    const croppedCanvas = document.createElement('canvas')
    const croppedCtx = croppedCanvas.getContext('2d')
    
    const { x, y, width, height } = boundingBox
    
    // Set the cropped canvas size
    croppedCanvas.width = width
    croppedCanvas.height = height
    
    // Draw the cropped portion
    croppedCtx.drawImage(
      canvas,
      x, y, width, height,  // Source rectangle
      0, 0, width, height   // Destination rectangle
    )
    
    return croppedCanvas.toDataURL('image/png')
  }, [])

  /**
   * Get the bounding box of non-transparent pixels in the canvas
   */
  const getDrawingBoundingBox = useCallback((canvas) => {
    const ctx = canvas.getContext('2d')
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const data = imageData.data
    
    let minX = canvas.width
    let minY = canvas.height
    let maxX = -1
    let maxY = -1
    
    // Find the bounding box of non-transparent pixels
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const index = (y * canvas.width + x) * 4
        const alpha = data[index + 3]
        
        if (alpha > 0) { // Non-transparent pixel
          minX = Math.min(minX, x)
          minY = Math.min(minY, y)
          maxX = Math.max(maxX, x)
          maxY = Math.max(maxY, y)
        }
      }
    }
    
    // If no content found, return null
    if (maxX === -1) {
      return null
    }
    
    // Add some padding around the content
    const padding = 10
    return {
      x: Math.max(0, minX - padding),
      y: Math.max(0, minY - padding),
      width: Math.min(canvas.width - Math.max(0, minX - padding), maxX - minX + 2 * padding),
      height: Math.min(canvas.height - Math.max(0, minY - padding), maxY - minY + 2 * padding)
    }
  }, [])

  /**
   * Undo last action
   */
  const undo = useCallback(() => {
    if (historyIndex <= 0) return
    
    setIsUndoRedo(true)
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    const newIndex = historyIndex - 1
    
    if (newIndex >= 0) {
      ctx.putImageData(history[newIndex], 0, 0)
    } else {
      // Clear canvas if going back to initial state
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      if (originalImage && mode === 'edit') {
        drawBackgroundImage(originalImage)
      }
    }
    
    setHistoryIndex(newIndex)
    
    // Notify parent of canvas change
    if (onDrawingChange) {
      onDrawingChange(canvas.toDataURL())
    }
    
    setTimeout(() => setIsUndoRedo(false), 100)
  }, [history, historyIndex, originalImage, mode, drawBackgroundImage, onDrawingChange])

  /**
   * Redo last undone action
   */
  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1) return
    
    setIsUndoRedo(true)
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    const newIndex = historyIndex + 1
    
    ctx.putImageData(history[newIndex], 0, 0)
    setHistoryIndex(newIndex)
    
    // Notify parent of canvas change
    if (onDrawingChange) {
      onDrawingChange(canvas.toDataURL())
    }
    
    setTimeout(() => setIsUndoRedo(false), 100)
  }, [history, historyIndex, onDrawingChange])

  /**
   * Handle text input completion
   */
  const handleTextComplete = useCallback((text) => {
    if (!text.trim()) {
      setIsTextMode(false)
      return
    }
    
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    
    // Set text styles
    ctx.font = `${brushSize * 4}px Arial`
    ctx.fillStyle = currentColor
    ctx.textAlign = 'left'
    ctx.textBaseline = 'top'
    
    // Enable antialiasing for smooth text rendering
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    
    // Draw text
    ctx.fillText(text, textPosition.x, textPosition.y)
    
    // Reset text mode
    setIsTextMode(false)
    setTextInput('')
    
    // Save to history
    setTimeout(() => saveToHistory(), 50)
    
    // Notify parent of canvas change
    if (onDrawingChange) {
      onDrawingChange(canvas.toDataURL())
    }
  }, [textPosition, brushSize, currentColor, saveToHistory, onDrawingChange])

  /**
   * Handle text input cancel
   */
  const handleTextCancel = useCallback(() => {
    setIsTextMode(false)
    setTextInput('')
  }, [])

  
  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    clearCanvas,
    resetCanvas,
    getCanvasDataUrl,
    undo,
    redo,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1
  }), [clearCanvas, resetCanvas, getCanvasDataUrl, undo, redo, historyIndex, history.length])
  
  // Mouse event handlers
  const handleMouseDown = startDrawing
  const handleMouseMove = draw
  const handleMouseUp = stopDrawing
  const handleMouseLeave = stopDrawing
  
  // Touch event handlers
  const handleTouchStart = (e) => {
    if (e.cancelable) {
      e.preventDefault()
    }
    // Use the first touch point
    const touch = e.touches[0]
    const mouseEvent = new MouseEvent('mousedown', {
      clientX: touch.clientX,
      clientY: touch.clientY,
      button: 0
    })
    startDrawing(mouseEvent)
  }
  
  const handleTouchMove = (e) => {
    if (e.cancelable) {
      e.preventDefault()
    }
    // Use the first touch point
    const touch = e.touches[0]
    const mouseEvent = new MouseEvent('mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY,
      button: 0
    })
    draw(mouseEvent)
  }
  
  const handleTouchEnd = (e) => {
    if (e.cancelable) {
      e.preventDefault()
    }
    stopDrawing(e)
  }
  
  // Add touch event listeners with proper options
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const options = { passive: false }
    
    canvas.addEventListener('touchstart', handleTouchStart, options)
    canvas.addEventListener('touchmove', handleTouchMove, options)
    canvas.addEventListener('touchend', handleTouchEnd, options)

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart)
      canvas.removeEventListener('touchmove', handleTouchMove)
      canvas.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd])

  return (
    <>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className={`drawing-canvas border-2 border-gray-300 dark:border-gray-600 rounded-lg touch-none ${
          currentTool === 'pan' ? 'cursor-grab' : 'cursor-crosshair'
        } ${isPanning ? 'cursor-grabbing' : ''}`}
        style={{
          width: `${width}px`,
          height: `${height}px`,
          touchAction: currentTool === 'pan' ? 'pan-x pan-y' : 'none', // Allow panning when pan tool is active
          flexShrink: 0, // Prevent shrinking in flex containers
          minWidth: `${width}px`,
          minHeight: `${height}px`,
          maxWidth: 'none',
          maxHeight: 'none'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      />
      
      {/* Text Input Modal */}
      {isTextMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Add Text
            </h3>
            <input
              ref={setTextInputRef}
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Enter text..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleTextComplete(textInput)
                } else if (e.key === 'Escape') {
                  handleTextCancel()
                }
              }}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={handleTextCancel}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleTextComplete(textInput)}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
              >
                Add Text
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
})

export default DrawingCanvas
